import { useEffect, useRef, useState } from "react";
import { Card, CardBody, CardHeader } from "../../../../../../../../_metronic/_partials/controls";
import { Button } from "react-bootstrap";
import { useGetUserSecurityQuestionsQuery, useSubmitUserSecurityQuestionAnswersMutation } from "../../../../../../../../redux/apiSlice";
import { CheckIcon } from "../../../../../../../util/Icons";

const SecurityQuestions = props => {
    const userId = props.match.params.userId;
    const requiredQuestionCount = 2;
    const [hasRequested, setHasRequested] = useState(false);
    const [questionSlots, setQuestionSlots] = useState([null, null]);
    const [fetchTarget, setFetchTarget] = useState(null);
    const [passedVerification, setPassedVerification] = useState(false);
    const lastQuestionsSignatureRef = useRef("");
    const {
        data: securityQuestions,
        isLoading,
        refetch
    } = useGetUserSecurityQuestionsQuery(
        { userId },
        { skip: !userId || !hasRequested }
    );
    const [answers, setAnswers] = useState({});
    const [submittingAnswers, setSubmittingAnswers] = useState({});
    const [answerStatus, setAnswerStatus] = useState({});
    const [submitErrorMessage, setSubmitErrorMessage] = useState(null);
    const [
        submitAnswers,
        { isLoading: isSubmitting, error: submitError }
    ] = useSubmitUserSecurityQuestionAnswersMutation();

    useEffect(() => {
        if (!securityQuestions || fetchTarget === null) {
            return;
        }
        const incomingQuestions = (securityQuestions || []).map((question, index) => ({
            id: question.answerId || question.securityQuestionId || `${index}`,
            answerId: question.answerId,
            securityQuestionId: question.securityQuestionId,
            userId: question.userId,
            userGroup: question.userGroup,
            question: question.question,
            created: question.created,
            updated: question.updated
        }));
        const signature = incomingQuestions.map((question) => question.id).join("|");
        if (signature && signature === lastQuestionsSignatureRef.current) {
            return;
        }
        if (fetchTarget === "all") {
            setQuestionSlots([
                incomingQuestions[0] || null,
                incomingQuestions[1] || null
            ]);
            setAnswers({});
            setSubmittingAnswers({});
            setAnswerStatus({});
            setPassedVerification(false);
        } else {
            setQuestionSlots(prev => {
                const next = [...prev];
                next[fetchTarget] = incomingQuestions[0] || next[fetchTarget];
                return next;
            });
            setAnswers(prev => ({ ...prev, [fetchTarget]: "" }));
            setSubmittingAnswers(prev => ({ ...prev, [fetchTarget]: false }));
            setAnswerStatus(prev => ({ ...prev, [fetchTarget]: null }));
            setPassedVerification(false);
        }
        lastQuestionsSignatureRef.current = signature || lastQuestionsSignatureRef.current;
        setFetchTarget(null);
    }, [securityQuestions, fetchTarget]);

    const handleFetchQuestions = () => {
        if (isLoading) {
            return;
        }
        setSubmitErrorMessage(null);
        setFetchTarget("all");
        if (!hasRequested) {
            setHasRequested(true);
            return;
        }
        refetch();
    };

    const handleFetchQuestionForSlot = (slotIndex) => {
        if (isLoading) {
            return;
        }
        setSubmitErrorMessage(null);
        setFetchTarget(slotIndex);
        refetch();
    };

    const totalCount = Math.min(requiredQuestionCount, questionSlots.filter(Boolean).length);
    const allCorrect = [0, 1].every((slotIndex) => questionSlots[slotIndex] && answerStatus[slotIndex] === "correct");

    useEffect(() => {
        if (allCorrect) {
            setPassedVerification(true);
            setQuestionSlots([null, null]);
        }
    }, [allCorrect]);

    const handleSubmitAnswer = async (slotIndex) => {
        const question = questionSlots[slotIndex];
        if (!question) {
            return;
        }
        const answerValue = (answers[slotIndex] || "").trim();
        if (!answerValue || submittingAnswers[slotIndex] || isSubmitting) {
            return;
        }
        setSubmitErrorMessage(null);
        setSubmittingAnswers(prev => ({ ...prev, [slotIndex]: true }));
        const payload = {
            securityQuestionAnswers: [{
                answerId: question.answerId || question.securityQuestionId,
                answer: answerValue,
                userId: question.userId || userId,
                userGroup: question.userGroup,
                question: question.question
            }]
        };
        try {
            const response = await submitAnswers(payload);
            const data = response?.data;
            const isCorrect = data?.verified === true || data?.correct === true || data?.correctAnswers === 1;
            if (isCorrect) {
                setAnswerStatus(prev => ({ ...prev, [slotIndex]: "correct" }));
            } else {
                setAnswerStatus(prev => ({ ...prev, [slotIndex]: "wrong" }));
            }
        } catch (error) {
            setSubmitErrorMessage("Unable to verify answer. Please try again.");
        } finally {
            setSubmittingAnswers(prev => ({ ...prev, [slotIndex]: false }));
        }
    };

    return (
        <div className="py-4 px-6">
            <Card className="mb-6">
                <CardHeader title="Security Questions" />
                <CardBody>
                    <div className="d-flex flex-wrap align-items-center mb-6">
                        <div className="text-muted font-weight-bold text-uppercase">Question Count</div>
                        <div className="font-weight-bolder ml-3">{totalCount}</div>
                    </div>
                    {!hasRequested ? (
                        <div className="d-flex justify-content-center">
                            <Button
                                type="button"
                                className="btn btn-primary btn-lg"
                                onClick={handleFetchQuestions}
                                disabled={isLoading}
                            >
                                Fetch Questions
                            </Button>
                        </div>
                    ) : isLoading ? (
                        <div className="alert alert-light mb-0">Loading security questions...</div>
                    ) : passedVerification ? (
                        <div className="d-flex flex-column align-items-center text-center py-8">
                            <div className="symbol symbol-80 symbol-light-success mb-4">
                                <span className="symbol-label">
                                    <CheckIcon size={36} className="text-success" />
                                </span>
                            </div>
                            <div className="font-weight-bolder font-size-h4 mb-2">Verification Passed</div>
                            <div className="text-muted mb-6" style={{ maxWidth: 520 }}>
                                Both answers were verified successfully. You can proceed with the user’s account actions.
                            </div>
                            <Button
                                type="button"
                                className="btn btn-primary btn-lg px-8"
                                onClick={handleFetchQuestions}
                                disabled={isLoading}
                            >
                                Fetch New Questions
                            </Button>
                        </div>
                    ) : totalCount === 0 ? (
                        <div className="alert alert-light mb-0">
                            No security questions available for this user.
                        </div>
                    ) : (
                        <>
                            {!submitErrorMessage && submitError && (
                                <div className="alert alert-danger mb-4">
                                    Unable to verify answer. Please try again.
                                </div>
                            )}
                            <div className="row">
                                {[0, 1].map((slotIndex) => {
                                    const question = questionSlots[slotIndex];
                                    if (!question) {
                                        return null;
                                    }
                                    const status = answerStatus[slotIndex];
                                    return (
                                        <div className="col-lg-6 mb-5" key={`slot-${slotIndex}`}>
                                            <div className="border rounded p-4 bg-light h-100">
                                                <div className="font-weight-bolder mb-2">
                                                    Question {slotIndex + 1}
                                                </div>
                                                <div className="text-dark mb-4">{question.question}</div>
                                                {!status && (
                                                    <>
                                                        <div className="form-group">
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-lg"
                                                                placeholder="Enter answer"
                                                                value={answers[slotIndex] || ""}
                                                                onChange={(event) =>
                                                                    setAnswers({ ...answers, [slotIndex]: event.target.value })
                                                                }
                                                            />
                                                        </div>
                                                        <Button
                                                            type="button"
                                                            className="btn btn-primary"
                                                            onClick={() => handleSubmitAnswer(slotIndex)}
                                                            disabled={!answers[slotIndex]?.trim() || submittingAnswers[slotIndex]}
                                                        >
                                                            {submittingAnswers[slotIndex] ? "Checking..." : "Answer"}
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            className="btn btn-light mt-3"
                                                            onClick={() => handleFetchQuestionForSlot(slotIndex)}
                                                            disabled={isLoading}
                                                        >
                                                            Fetch New Question
                                                        </Button>
                                                    </>
                                                )}
                                                {status === "correct" && (
                                                    <Button
                                                        type="button"
                                                        className="btn btn-success btn-lg btn-block font-weight-bolder"
                                                        disabled
                                                    >
                                                        <span className="d-inline-flex align-items-center justify-content-center">
                                                            <CheckIcon size={18} className="mr-2" />
                                                            Correct Answer
                                                        </span>
                                                    </Button>
                                                )}
                                                {status === "wrong" && (
                                                    <>
                                                        <Button
                                                            type="button"
                                                            className="btn btn-danger btn-lg btn-block"
                                                            disabled
                                                        >
                                                            Wrong Answer
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            className="btn btn-light mt-3"
                                                            onClick={() => handleFetchQuestionForSlot(slotIndex)}
                                                            disabled={isLoading}
                                                        >
                                                            Fetch New Question
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </CardBody>
            </Card>
        </div>
    );
};

export default SecurityQuestions;
